import React, { memo, useEffect, useMemo } from 'react'

import { commonGridColDefs } from '@/entities/columnDefinitions/commonColDefs'
import { getGridColDefs } from '@/entities/columnDefinitions/libs/getColDef'
import { setRunsheetShiftTableLoading } from '@/entities/gridTables/slices/runsheetShiftTablesSlice'
import { setRunsheetUnassignedTableLoading } from '@/entities/gridTables/slices/runsheetUnassignedTablesSlice'
import { moveBreak } from '@/entities/gridTables/thunks/moveBreak'
import { processDropOnShift, ProcessDropOnShiftPayload } from '@/entities/gridTables/thunks/processDropOnShift'
import { updateRunsheetShiftTableState } from '@/entities/gridTables/thunks/updateRunsheetShiftTableState'
import { updateRunsheetUnassignedTableState } from '@/entities/gridTables/thunks/updateRunsheetUnassignedTableState'
import { fetchRunsheetOrdersByShift } from '@/entities/orders/thunks/fetchRunsheetOrdersByShift'
import { fetchRunsheets, runsheetShiftsSelectors, runsheetsSelectors, StopTypeEnum } from '@/entities/schedules'
import { selectBusinessModel } from '@/entities/user'
import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { openRightSidebar, setLastSelectedStopId } from '@/entities/userInterface/slices/userInterfaceSlice'
import { setDetailViewedOrder } from '@/entities/userInterface/thunks/setDetailViewedOrder'
import { upsertOneWidgetWindow } from '@/entities/widgetWindows'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { GridRowActionItem } from '@/shared/components/GridTable/models/GridRowActions'
import { GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { TREE_WIDGETS, WidgetTypeWithSuffix } from '@/shared/components/Mosaic/models/widgetType'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faCarSide, faTruck, faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import { Typography } from '@mui/material'
import { GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-premium'

import { useColumnGroupingModel } from './libs/useColumnGroupingModel'
import { useRunsheetShiftTableData } from './libs/useRunsheetShiftTableData'
import { useRunsheetShiftTableContext } from './RunsheetShiftTableContext'

export type RunsheetShiftTableProps = {
  widgetId: WidgetTypeWithSuffix // Widget Window id
  id: string // Runsheet Shift id
}

// TODO:
//   1: Optimize Grid further by preventing it from being re-rendered when the parent window changes
//   2: Use React Portal to move the component around without destroying the component and re-rendering
export const RunsheetShiftTable: React.FC<RunsheetShiftTableProps> = memo(({ widgetId, id }) => {
  const { runsheetShift, rows, isLoading } = useRunsheetShiftTableData(id)
  const { columnGroupingModel, initialColumnGroupVisibility } = useColumnGroupingModel()
  const { setShiftId, setRunsheetId } = useRunsheetShiftTableContext()
  const businessModel = useAppSelector(selectBusinessModel)
  const shift = useAppSelector(state => runsheetShiftsSelectors.selectById(state, id))
  const runsheet = useAppSelector(state => runsheetsSelectors.selectById(state, shift?.runsheetId || ''))
  const runsheetName = runsheet?.name
  const selectedSearchResult = useAppSelector(state => state.userInterface.selectedSearchResult)
  const apiRef = useGridApiRef()
  const columns = getGridColDefs({
    gridTypeName: 'runsheetShift',
    override: {
      //Override the default column definition for etaTime to include planned arrival time
      etaTime: {
        field: 'etaTime',
        headerName: 'PTA [ETA]',
        description: 'Planned Time Arrival [Estimated Time Arrival]',
        width: 70,
        minWidth: 40,
        filterable: true,
        sortable: false,
        pinnable: false,
        type: 'string',
      },
    },
    additionalColDefs: [commonGridColDefs.geocodeError],
  })

  const dispatch = useAppDispatch()

  // Update global store with widow title, uiColor, etc.
  useEffect(() => {
    if (runsheetShift) {
      dispatch(upsertOneWidgetWindow({
        id: widgetId,
        runsheetId: shift?.runsheetId || '',
        runsheetDate: runsheetName,
        isHistorical: Boolean(runsheet?.closedOn),
        shiftId: id,
        isActive: true,
        widgetType: TREE_WIDGETS.RunsheetShiftTable,
        navigations: [
          {
            title: runsheetName || '',
          },
          {
            title: runsheetShift.name,
            icon: (businessModel === BusinessModelEnum.Depot) ? faTruck : faCarSide,
            uiColor: runsheetShift.uiColor || 'black',
          },
        ],
      }))
    }
  }, [dispatch, widgetId, runsheetName, runsheetShift, businessModel, id, runsheet?.closedOn, shift?.runsheetId])

  // Update Selected rows when search result change
  useEffect(() => {
    if (apiRef.current && selectedSearchResult && !selectedSearchResult.isPlanning && selectedSearchResult.shiftId == id &&
      selectedSearchResult.stopId && apiRef?.current?.getRow(selectedSearchResult.stopId)) {
      apiRef.current?.selectRows([selectedSearchResult.stopId], true, true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, selectedSearchResult, id])

  // Fetch orders by shift
  useEffect(() => {
    // TODO: Update API Endpoint to retrieve orders directly from shift id instead of OrderIds and call it before rendering this component if possible
    dispatch(fetchRunsheetOrdersByShift({ shiftId: id }))
  }, [dispatch, id])

  // Update shared context between the table and the toolbar
  useEffect(() => {
    setShiftId(parseInt(id))
    setRunsheetId(shift?.runsheetId || '')
  }, [id, setShiftId, setRunsheetId, shift?.runsheetId])

  const onGridRowClick = (selectedRowData: GridValidRowModel) => {
    dispatch(setLastSelectedStopId(selectedRowData.id))
  }

  const onGridRowsDrop = async (sourceRowData: GridValidRowModel[], targetRowData: GridValidRowModel) => {
    const sourceType = sourceRowData[0]?.dragType
    const isSourceBreak = sourceRowData[0]?.stopType === StopTypeEnum.Break
    let payload: ProcessDropOnShiftPayload | null = null
    let refreshUnassignedOrders = false

    if (sourceType === GridTableDragType.SHIFT_STOP_ROW) {
      const sourceShiftId = sourceRowData[0].shiftId
      const targetShiftId = targetRowData.shiftId
      const sourceStopIds = sourceRowData.map((row) => row.id)
      const targetStopId = targetRowData.id

      payload = {
        orderIds: null,
        sourceShiftId,
        shiftId: targetShiftId,
        stopId: targetStopId,
        stopIds: sourceStopIds,
      }
    } else if (sourceType === GridTableDragType.UNASSIGNED_ORDER_ROW) {
      const orderIds = sourceRowData.map((row) => row.orderId)
      const targetShiftId = targetRowData.shiftId
      const targetStopId = targetRowData.id

      refreshUnassignedOrders = true

      payload = {
        orderIds,
        sourceShiftId: null,
        shiftId: targetShiftId,
        stopId: targetStopId,
        stopIds: null,
      }
    }

    if (payload === null) return

    // Set loading state
    dispatch(setRunsheetShiftTableLoading({ id: targetRowData.shiftId, isLoading: true }))

    if (payload.sourceShiftId) {
      dispatch(setRunsheetShiftTableLoading({ id: payload.sourceShiftId, isLoading: true }))
    } else if (refreshUnassignedOrders) {
      dispatch(setRunsheetUnassignedTableLoading({ runsheetId: sourceRowData[0].runsheetId, isLoading: true }))
    }

    // Process drop on shift
    if (isSourceBreak) {
      await dispatch(moveBreak({ stopId: sourceRowData[0].id, targetStopId: targetRowData.id }))
      await dispatch(fetchRunsheets({ runsheetIds: [sourceRowData[0].runsheetId] }))
    } else {
      await dispatch(processDropOnShift(payload))
      await dispatch(fetchRunsheets({ runsheetIds: [targetRowData.runsheetId, sourceRowData[0].runsheetId] }))
    }
    
    await Promise.allSettled([
      dispatch(updateRunsheetShiftTableState({ shiftId: targetRowData.shiftId })),
      payload.sourceShiftId ? dispatch(updateRunsheetShiftTableState({ shiftId: payload.sourceShiftId })) : Promise.resolve(),
      refreshUnassignedOrders ? dispatch(updateRunsheetUnassignedTableState({ runsheetId: targetRowData.runsheetId })) : Promise.resolve(),
    ])

    // Reset loading state
    dispatch(setRunsheetShiftTableLoading({ id: targetRowData.shiftId, isLoading: false }))

    if (payload.sourceShiftId) {
      dispatch(setRunsheetShiftTableLoading({ id: payload.sourceShiftId, isLoading: false }))
    } else if (refreshUnassignedOrders) {
      dispatch(setRunsheetUnassignedTableLoading({ runsheetId: sourceRowData[0].runsheetId, isLoading: false }))
    }
  }

  const viewOrderDetailAction: GridRowActionItem = {
    icon: faUpRightFromSquare,
    label: 'View Order',
    onClick: (rowData: GridValidRowModel) => {
      if (rowData.orderId) {
        dispatch(openRightSidebar())
        dispatch(setDetailViewedOrder({ orderId: rowData.orderId }))
      }
    },
  }

  const footerSummary = useMemo(() => (
    <React.Fragment>
      Total:&nbsp;
      Orders: <strong>{runsheetShift?.totalQuantity}</strong>&nbsp;&nbsp;
      Dist: <strong>{runsheetShift?.totalDistance}</strong>&nbsp;&nbsp;
      Cost: <strong>{runsheetShift?.totalCost}</strong>&nbsp;&nbsp;
      Wgt: <strong>{runsheetShift?.totalWeight}</strong>&nbsp;&nbsp;
      Vol: <strong>{runsheetShift?.totalVolume}</strong>&nbsp;&nbsp;
      Time: <strong>{runsheetShift?.totalWorkingTime}</strong>&nbsp;&nbsp;
    </React.Fragment>
  ), [runsheetShift?.totalQuantity, runsheetShift?.totalDistance, runsheetShift?.totalCost,
    runsheetShift?.totalVolume, runsheetShift?.totalWeight, runsheetShift?.totalWorkingTime])


  if (!rows) {
    return <Typography variant="body1">Loading...</Typography>
  }

  return (
    <GridTable
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      columnGroupingModel={columnGroupingModel}
      checkboxSelection
      isDragDropEnabled
      isRowActionsEnabled
      loading={isLoading}
      disableVirtualization
      onRowClick={onGridRowClick}
      onGridRowsDrop={onGridRowsDrop} // Drag and Drop
      pinnedLeftColumns={['geocodeError']}
      hotAction={viewOrderDetailAction}
      initialColumnGroupVisibility={initialColumnGroupVisibility}
      slotProps={{
        footer: {
          summary: footerSummary,
        },
      }}
    />
  )
}, (prevProps, nextProps) => {
  return prevProps.id === nextProps.id && prevProps.widgetId === nextProps.widgetId
})

